import React, { useState } from 'react';
import { ArrowLeftIcon, LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';

const AdminLogin = ({ onLoginSuccess, backendUrl, onBack }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedOut, setLockedOut] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (lockedOut) {
      const now = new Date();
      if (lockoutEndTime && now < lockoutEndTime) {
        const remainingSeconds = Math.ceil((lockoutEndTime - now) / 1000);
        setError(`Too many failed attempts. Please try again in ${remainingSeconds} seconds.`);
        return;
      } else {
        setLockedOut(false);
        setAttempts(0);
      }
    }

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${backendUrl}/api/admin/login`, {
        username: username.trim(),
        password: password.trim()
      });

      if (response.data.success && response.data.token) {
        sessionStorage.setItem('adminToken', response.data.token);
        onLoginSuccess();
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Admin login error:', error);

      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 5) {
        const lockoutEnd = new Date(Date.now() + 15 * 60 * 1000);
        setLockoutEndTime(lockoutEnd);
        setLockedOut(true);
        setError('Too many failed attempts. Please try again in 15 minutes.');
      } else if (error.response?.status === 401) {
        setError(`Invalid credentials. ${5 - newAttempts} attempts remaining.`);
      } else if (error.response?.status === 429) {
        setError('Too many requests. Please try again later.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <button className="btn btn-secondary back-btn-admin" onClick={onBack}>
          <ArrowLeftIcon style={{ marginRight: '8px' }} />
          Back to App
        </button>

        <div className="admin-login-header">
          <div className="admin-icon">
            <LockClosedIcon width={32} height={32} />
          </div>
          <h1>Admin Access</h1>
          <p>Enter your credentials to access the admin panel</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && (
            <div className="admin-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              disabled={loading || lockedOut}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={loading || lockedOut}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary admin-login-btn"
            disabled={loading || lockedOut}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="admin-login-footer">
          <p>This area is restricted to authorized personnel only.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
